// src/components/editable/ImageLightbox.tsx
// ---------------------------------------------------------------------------
// In-app image lightbox carousel for clinical images.
//
// Replaces the previous `window.open(url, "_blank")` behavior for the Imaging
// panel thumbnails and the Diagnosis lesion-image gallery. Shows one image at a
// time inside a dark full-screen overlay with:
//   - zoom in / out + pan (react-zoom-pan-pinch, same lib as DocumentViewer)
//   - prev / next navigation (arrow buttons + keyboard arrows), clamped to bounds
//   - a "current / total" counter
//   - close via the X button, backdrop click, or Esc
//
// Zoom/pan state is reset whenever the visible image changes (the TransformWrapper
// is keyed on the active index, so it remounts fresh at scale 1). Colors come from
// the Athena palette; no hardcoded hex, no inline style, no CSS files.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect } from "react";
import { alpha, Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
} from "@mui/icons-material";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { athenaColors } from "../../theme/palette";
import { useTranslation } from "../../i18n";

interface ImageLightboxProps {
  /** Whether the overlay is visible. */
  open: boolean;
  /** Ordered list of image URLs to page through. */
  images: string[];
  /** Index of the currently displayed image within `images`. */
  index: number;
  /** Called with the new index when the user navigates prev/next. */
  onIndexChange: (index: number) => void;
  /** Close the overlay (X button, backdrop click, or Esc). */
  onClose: () => void;
}

const controlButtonSx = {
  color: alpha(athenaColors.white, 0.85),
  backgroundColor: alpha(athenaColors.deepNavy, 0.55),
  border: `1px solid ${alpha(athenaColors.white, 0.18)}`,
  backdropFilter: "blur(6px)",
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover": {
    color: athenaColors.white,
    backgroundColor: alpha(athenaColors.periwinkle, 0.4),
  },
  "&.Mui-disabled": {
    color: alpha(athenaColors.white, 0.25),
    backgroundColor: alpha(athenaColors.deepNavy, 0.35),
  },
};

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  images,
  index,
  onIndexChange,
  onClose,
}) => {
  const { t } = useTranslation();

  const total = images.length;
  // Clamp the incoming index defensively so an out-of-range value never blanks
  // the overlay (e.g. if the underlying list shrinks while open).
  const safeIndex = Math.min(Math.max(index, 0), Math.max(total - 1, 0));
  const currentUrl = images[safeIndex];
  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < total - 1;

  const goPrev = useCallback(() => {
    if (safeIndex > 0) onIndexChange(safeIndex - 1);
  }, [safeIndex, onIndexChange]);

  const goNext = useCallback(() => {
    if (safeIndex < total - 1) onIndexChange(safeIndex + 1);
  }, [safeIndex, total, onIndexChange]);

  // Keyboard navigation while the overlay is open. Arrows page prev/next; Esc is
  // also handled by the Modal itself but we keep it here for parity.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, goPrev, goNext, onClose]);

  if (!currentUrl) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label={t("imaging.lightbox_title", "Image viewer")}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(athenaColors.black, 0.85),
            backdropFilter: "blur(6px)",
          },
        },
      }}
    >
      <Box
        // Backdrop click (anywhere not on the image/controls) closes the overlay.
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
        {/* Top bar: counter + zoom controls + close */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            zIndex: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: alpha(athenaColors.white, 0.8), fontWeight: 600, letterSpacing: 0.5 }}
          >
            {total > 1 ? `${safeIndex + 1} / ${total}` : ""}
          </Typography>
          <Tooltip title={t("imaging.lightbox_close", "Close")} arrow>
            <IconButton onClick={onClose} sx={controlButtonSx} aria-label={t("imaging.lightbox_close", "Close")}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Prev arrow */}
        {total > 1 && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ position: "absolute", left: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", px: 1.5, zIndex: 2 }}
          >
            <Tooltip title={t("imaging.lightbox_prev", "Previous")} arrow>
              <span>
                <IconButton
                  onClick={goPrev}
                  disabled={!hasPrev}
                  sx={controlButtonSx}
                  aria-label={t("imaging.lightbox_prev", "Previous")}
                >
                  <ChevronLeft />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Next arrow */}
        {total > 1 && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", px: 1.5, zIndex: 2 }}
          >
            <Tooltip title={t("imaging.lightbox_next", "Next")} arrow>
              <span>
                <IconButton
                  onClick={goNext}
                  disabled={!hasNext}
                  sx={controlButtonSx}
                  aria-label={t("imaging.lightbox_next", "Next")}
                >
                  <ChevronRight />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Zoomable / pannable image. Keyed on the active index so the zoom+pan
            transform resets to scale 1 whenever the visible image changes. */}
        <TransformWrapper
          key={safeIndex}
          wheel={{ step: 0.15 }}
          pinch={{ step: 0.15 }}
          doubleClick={{ mode: "reset" }}
          centerOnInit
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  zIndex: 2,
                }}
              >
                <Tooltip title={t("imaging.lightbox_zoom_in", "Zoom in")} arrow>
                  <IconButton onClick={() => zoomIn()} sx={controlButtonSx} aria-label={t("imaging.lightbox_zoom_in", "Zoom in")}>
                    <ZoomIn />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("imaging.lightbox_zoom_out", "Zoom out")} arrow>
                  <IconButton onClick={() => zoomOut()} sx={controlButtonSx} aria-label={t("imaging.lightbox_zoom_out", "Zoom out")}>
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("imaging.lightbox_reset", "Reset view")} arrow>
                  <IconButton onClick={() => resetTransform()} sx={controlButtonSx} aria-label={t("imaging.lightbox_reset", "Reset view")}>
                    <CenterFocusStrong />
                  </IconButton>
                </Tooltip>
              </Box>
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{ width: "100%", height: "100%" }}
              >
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={currentUrl}
                    alt={t("imaging.image_alt", "Imaging study")}
                    sx={{
                      maxWidth: "88vw",
                      maxHeight: "82vh",
                      objectFit: "contain",
                      borderRadius: 1,
                      boxShadow: `0 12px 48px ${alpha(athenaColors.black, 0.6)}`,
                      userSelect: "none",
                    }}
                  />
                </Box>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </Box>
    </Modal>
  );
};

export default ImageLightbox;
